import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dropdown, message, Modal, Segmented, Spin } from "antd";
import type { MenuProps } from "antd";
import { ApartmentOutlined, BankOutlined, MoreOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { usePartnershipContext } from "../../../../core/context/PartnershipContext";
import EntityService, {
  CreateEntityDto,
  EntityDto,
} from "../../services/EntityService";
import TierList from "../../../TieringView/components/TierList/TierList";
import TierDiagram from "../../../TieringView/components/TierDiagram/TierDiagram";
import { MODAL_STYLES, MODAL_TITLE_STYLE } from "../../../../styles/modalStyles";
import styles from "./EntityList.module.css";

// ── Types ──────────────────────────────────────────────────────────────────────

type TabKey = "all" | "partnerships" | "partners" | "tiering";

const TABS: { key: TabKey; label: string }[] = [
  { key: "all",          label: "All"           },
  { key: "partnerships", label: "Partnerships"  },
  { key: "partners",     label: "Partners"      },
  { key: "tiering",      label: "Tiering"       },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function EntityTypeBadge({ type }: { type?: string }) {
  if (!type) return <span className={styles.cellGap}>—</span>;
  if (type === "Partnership") {
    return <span className={styles.badgePartnership}>Partnership</span>;
  }
  return <span className={styles.badgePartner}>Partner</span>;
}

function StatusBadge({ status }: { status?: string }) {
  if (!status) return <span className={styles.cellGap}>—</span>;
  return (
    <span className={status === "Active" ? styles.badgeActive : styles.badgeInactive}>
      {status}
    </span>
  );
}

// ── Partnership create form ────────────────────────────────────────────────────

interface PartnershipFormData {
  name: string;
  ein: string;
  category: string;
  taxYearStart: string;
  status: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  notes: string;
}

const PartnershipForm: React.FC<{
  initial?: Partial<PartnershipFormData>;
  onCancel: () => void;
  onSave: (data: CreateEntityDto) => void;
  saving: boolean;
}> = ({ initial = {}, onCancel, onSave, saving }) => {
  const [form, setForm] = useState<PartnershipFormData>({
    name:        initial.name        ?? "",
    ein:         initial.ein         ?? "",
    category:    initial.category    ?? "Limited Partnership",
    taxYearStart:initial.taxYearStart ?? "January 1",
    status:      initial.status      ?? "Active",
    address:     initial.address     ?? "",
    city:        initial.city        ?? "",
    state:       initial.state       ?? "",
    zipCode:     initial.zipCode     ?? "",
    notes:       initial.notes       ?? "",
  });

  const set = (k: keyof PartnershipFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(prev => ({ ...prev, [k]: e.target.value }));

  const handleSave = () => {
    if (!form.name.trim()) { message.error("Partnership name is required"); return; }
    onSave({
      name: form.name.trim(),
      entityType: "Partnership",
      einSsn: form.ein,
      category: form.category,
      taxYearStart: form.taxYearStart,
      address: form.address,
      city: form.city,
      state: form.state,
      zipCode: form.zipCode,
      notes: form.notes,
    });
  };

  return (
    <div className={styles.formStack}>
      <div className={styles.formRow}>
        <label className={styles.formLabel}>Entity Name <span className={styles.req}>*</span></label>
        <input className={styles.formInput} value={form.name} onChange={set("name")} placeholder="e.g. TechCorp Global LP" />
      </div>
      <div className={styles.formRow}>
        <label className={styles.formLabel}>EIN</label>
        <input className={styles.formInput} value={form.ein} onChange={set("ein")} placeholder="12-3456789" />
        <div className={styles.formHint}>Format: XX-XXXXXXX</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>Tax Entity Type</label>
          <select className={styles.formSelect} value={form.category} onChange={set("category")}>
            <option>Limited Partnership</option>
            <option>LLC</option>
            <option>S-Corporation</option>
            <option>C-Corporation</option>
            <option>General Partnership</option>
          </select>
        </div>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>Tax Year Start</label>
          <select className={styles.formSelect} value={form.taxYearStart} onChange={set("taxYearStart")}>
            <option>January 1</option>
            <option>April 1</option>
            <option>July 1</option>
            <option>October 1</option>
          </select>
        </div>
      </div>
      <div className={styles.formRow}>
        <label className={styles.formLabel}>Status</label>
        <select className={styles.formSelect} value={form.status} onChange={set("status")}>
          <option>Active</option>
          <option>Inactive</option>
        </select>
      </div>
      <div className={styles.formRow}>
        <label className={styles.formLabel}>Street Address</label>
        <input className={styles.formInput} value={form.address} onChange={set("address")} placeholder="1200 Innovation Way, Suite 400" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>City</label>
          <input className={styles.formInput} value={form.city} onChange={set("city")} placeholder="Palo Alto" />
        </div>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>State</label>
          <input className={styles.formInput} value={form.state} onChange={set("state")} placeholder="CA" maxLength={2} />
        </div>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>ZIP Code</label>
          <input className={styles.formInput} value={form.zipCode} onChange={set("zipCode")} placeholder="94304" />
        </div>
      </div>
      <div className={styles.formRow}>
        <label className={styles.formLabel}>Notes</label>
        <textarea className={styles.formTextarea} rows={3} value={form.notes} onChange={set("notes")} placeholder="Notes about this entity..." />
      </div>
      <div className={styles.formActions}>
        <button className={styles.btnOutline} onClick={onCancel} disabled={saving}>Cancel</button>
        <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Entity"}
        </button>
      </div>
    </div>
  );
};

// ── Partner create form ────────────────────────────────────────────────────────

interface PartnerFormData {
  name: string;
  ssn: string;
  category: string;
  status: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  notes: string;
}

const PartnerForm: React.FC<{
  initial?: Partial<PartnerFormData>;
  onCancel: () => void;
  onSave: (data: CreateEntityDto) => void;
  saving: boolean;
}> = ({ initial = {}, onCancel, onSave, saving }) => {
  const [form, setForm] = useState<PartnerFormData>({
    name:    initial.name    ?? "",
    ssn:     initial.ssn     ?? "",
    category:initial.category ?? "Individual",
    status:  initial.status   ?? "Active",
    address: initial.address  ?? "",
    city:    initial.city     ?? "",
    state:   initial.state    ?? "",
    zipCode: initial.zipCode  ?? "",
    notes:   initial.notes    ?? "",
  });
  const [showSsn, setShowSsn] = useState(false);

  const set = (k: keyof PartnerFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(prev => ({ ...prev, [k]: e.target.value }));

  const handleSave = () => {
    if (!form.name.trim()) { message.error("Full name is required"); return; }
    onSave({
      name: form.name.trim(),
      entityType: "Partner",
      einSsn: form.ssn,
      category: form.category,
      address: form.address,
      city: form.city,
      state: form.state,
      zipCode: form.zipCode,
      notes: form.notes,
    });
  };

  return (
    <div className={styles.formStack}>
      <div className={styles.formRow}>
        <label className={styles.formLabel}>Full Name <span className={styles.req}>*</span></label>
        <input className={styles.formInput} value={form.name} onChange={set("name")} placeholder="e.g. James Worthington" />
      </div>
      <div className={styles.formRow}>
        <div className={styles.ssnHead}>
          <label className={styles.formLabel} style={{ marginBottom: 0 }}>SSN</label>
          <span className={styles.encryptedBadge}>🔒 Encrypted</span>
        </div>
        <div style={{ position: "relative" }}>
          <input
            className={styles.formInput}
            type={showSsn ? "text" : "password"}
            value={form.ssn}
            onChange={set("ssn")}
            placeholder="123-45-6789"
            style={{ paddingRight: 38 }}
          />
          <button type="button" className={styles.ssnToggle} onClick={() => setShowSsn(s => !s)}>
            {showSsn ? "👁" : "👁‍🗨"}
          </button>
        </div>
        <div className={styles.formHint}>SSN is encrypted at rest. Only last 4 digits visible after save.</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>Tax Entity Type</label>
          <select className={styles.formSelect} value={form.category} onChange={set("category")}>
            <option>Individual</option>
            <option>Trust</option>
            <option>Corporation</option>
            <option>Partnership</option>
          </select>
        </div>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>Status</label>
          <select className={styles.formSelect} value={form.status} onChange={set("status")}>
            <option>Active</option>
            <option>Inactive</option>
          </select>
        </div>
      </div>
      <div className={styles.formRow}>
        <label className={styles.formLabel}>Street Address</label>
        <input className={styles.formInput} value={form.address} onChange={set("address")} placeholder="123 Main St" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>City</label>
          <input className={styles.formInput} value={form.city} onChange={set("city")} placeholder="New York" />
        </div>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>State</label>
          <input className={styles.formInput} value={form.state} onChange={set("state")} placeholder="NY" maxLength={2} />
        </div>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>ZIP Code</label>
          <input className={styles.formInput} value={form.zipCode} onChange={set("zipCode")} placeholder="10001" />
        </div>
      </div>
      <div className={styles.formRow}>
        <label className={styles.formLabel}>Notes</label>
        <textarea className={styles.formTextarea} rows={3} value={form.notes} onChange={set("notes")} placeholder="Notes about this partner..." />
      </div>
      <div className={styles.formActions}>
        <button className={styles.btnOutline} onClick={onCancel} disabled={saving}>Cancel</button>
        <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Partner"}
        </button>
      </div>
    </div>
  );
};

// ── Entity Type Selector Modal (3.0) ───────────────────────────────────────────

const cardBase: React.CSSProperties = {
  background: 'rgba(26,39,68,0.60)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 12,
  padding: '28px 24px',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  textAlign: 'center',
  flex: 1,
};
const cardHovered: React.CSSProperties = {
  ...cardBase,
  background: 'rgba(26,39,68,0.85)',
  border: '1px solid rgba(242,101,34,0.40)',
};

const EntityTypeModal: React.FC<{
  onClose: () => void;
  onSelect: (type: "partnership" | "partner") => void;
}> = ({ onClose, onSelect }) => {
  const [hoverCard, setHoverCard] = useState<"partnership" | "partner" | null>(null);

  const TYPE_CARDS: { key: "partnership" | "partner"; icon: React.ReactNode; title: string; sub: string }[] = [
    {
      key: "partnership",
      icon: <BankOutlined style={{ fontSize: 32, color: '#F26522' }} />,
      title: "Partnership",
      sub: "A legal entity (LP, LLC, S-Corp) that files its own return",
    },
    {
      key: "partner",
      icon: <ApartmentOutlined style={{ fontSize: 32, color: '#F26522' }} />,
      title: "Partner",
      sub: "An individual or entity that holds interest in a partnership",
    },
  ];

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={<span style={MODAL_TITLE_STYLE}>Add Entity</span>}
      centered
      width={440}
      styles={MODAL_STYLES}
    >
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, margin: '0 0 20px' }}>
        What type of entity would you like to create?
      </p>
      <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
        {TYPE_CARDS.map(({ key, icon, title, sub }) => (
          <div
            key={key}
            style={hoverCard === key ? cardHovered : cardBase}
            onClick={() => onSelect(key)}
            onMouseEnter={() => setHoverCard(key)}
            onMouseLeave={() => setHoverCard(null)}
          >
            <div style={{ fontSize: 32, color: '#F26522', marginBottom: 12 }}>{icon}</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginTop: 8 }}>{title}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>{sub}</div>
          </div>
        ))}
      </div>
    </Modal>
  );
};

// ── Create / Edit Modal ────────────────────────────────────────────────────────

interface CreateEditModalProps {
  mode: "create" | "edit";
  entityType: "partnership" | "partner";
  entity?: EntityDto;
  clientId?: string;
  onClose: () => void;
  onSaved: () => void;
}

const CreateEditModal: React.FC<CreateEditModalProps> = ({
  mode, entityType, entity, clientId, onClose, onSaved,
}) => {
  const [saving, setSaving] = useState(false);

  const handleSave = async (dto: CreateEntityDto) => {
    setSaving(true);
    try {
      if (mode === "create") {
        await EntityService.createEntity({ ...dto, clientId });
      } else if (entity) {
        await EntityService.updateEntity(entity.id, dto);
      }
      message.success(`Entity ${mode === "create" ? "created" : "updated"} successfully`);
      onSaved();
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : "An error occurred";
      message.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const title = mode === "create"
    ? (entityType === "partnership" ? "Create Partnership" : "Create Partner")
    : "Edit Entity";

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={<span style={MODAL_TITLE_STYLE}>{title}</span>}
      centered
      width={560}
      styles={MODAL_STYLES}
    >
      <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 4 }}>
        {entityType === "partnership"
          ? <PartnershipForm onCancel={onClose} onSave={handleSave} saving={saving} initial={{
              name:         entity?.name,
              ein:          entity?.einSsn,
              category:     entity?.category,
              taxYearStart: entity?.taxYearStart,
              status:       entity?.status,
              address:      entity?.address,
              city:         entity?.city,
              state:        entity?.state,
              zipCode:      entity?.zipCode,
            }} />
          : <PartnerForm onCancel={onClose} onSave={handleSave} saving={saving} initial={{
              name:    entity?.name,
              ssn:     entity?.einSsn,
              category:entity?.category,
              status:  entity?.status,
              address: entity?.address,
              city:    entity?.city,
              state:   entity?.state,
              zipCode: entity?.zipCode,
            }} />
        }
      </div>
    </Modal>
  );
};

// ── EntityList ─────────────────────────────────────────────────────────────────

export const EntityList: React.FC = () => {
  const navigate = useNavigate();
  const { selectedClient, setSelectedPartnership, selectedPartnership } = usePartnershipContext();

  // ── Data
  const [entities, setEntities]   = useState<EntityDto[]>([]);
  const [loading, setLoading]     = useState(false);
  const prevClientRef             = useRef<string | null>(null);

  // ── UI
  const [searchText, setSearchText]     = useState("");
  const [activeTab, setActiveTab]       = useState<TabKey>("all");
  const [tierView, setTierView]         = useState<string | number>("List");

  // ── Create flow
  const [showTypeModal, setShowTypeModal]       = useState(false);
  const [entityTypeChoice, setEntityTypeChoice] = useState<"partnership" | "partner">("partnership");
  const [showCreateModal, setShowCreateModal]   = useState(false);

  // ── Edit flow
  const [editTarget, setEditTarget]             = useState<EntityDto | null>(null);
  const [editTypeChoice, setEditTypeChoice]     = useState<"partnership" | "partner">("partnership");
  const [showEditModal, setShowEditModal]       = useState(false);

  const fetchEntities = useCallback(async (clientId: string) => {
    setLoading(true);
    try {
      const list = await EntityService.getEntitiesByClient(clientId);
      setEntities(list);
    } catch {
      message.error("Failed to load entities");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const clientId = selectedClient?.id ?? null;
    if (clientId !== null && clientId !== prevClientRef.current) {
      prevClientRef.current = clientId;
      fetchEntities(clientId);
    }
    if (clientId === null) {
      prevClientRef.current = null;
      setEntities([]);
    }
  }, [selectedClient, fetchEntities]);

  // ── Counts per tab (entityType now returned by API)
  const counts: Partial<Record<TabKey, number>> = {
    all:           entities.length,
    partnerships:  entities.filter(e => e.entityType === "Partnership").length,
    partners:      entities.filter(e => e.entityType === "Partner").length,
  };

  // ── Filtered list — by tab then by search
  const tabFiltered = entities.filter(e => {
    if (activeTab === "partnerships") return e.entityType === "Partnership";
    if (activeTab === "partners")     return e.entityType === "Partner";
    return true;
  });
  const search = searchText.toLowerCase();
  const filtered = tabFiltered.filter(e =>
    e.name.toLowerCase().includes(search) ||
    `ent-${String(e.id).padStart(3, "0")}`.includes(search) ||
    (e.einSsn ?? "").toLowerCase().includes(search)
  );

  // ── Open Edit modal for a record
  const openEdit = (record: EntityDto) => {
    setEditTarget(record);
    setEditTypeChoice(
      record.entityType?.toLowerCase() === "partner" ? "partner" : "partnership"
    );
    setShowEditModal(true);
  };

  // ── Actions dropdown for each row
  const rowMenu = (record: EntityDto): MenuProps["items"] => [
    {
      key: "edit",
      label: "Edit",
      onClick: ({ domEvent }) => { domEvent.stopPropagation(); openEdit(record); },
    },
    {
      key: "deactivate",
      label: "Deactivate",
      danger: true,
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        Modal.confirm({
          title: "Deactivate entity?",
          content: `This will mark "${record.name}" as inactive.`,
          okText: "Deactivate",
          okButtonProps: { danger: true },
          onOk: async () => {
            try {
              await EntityService.deactivateEntity(record.id);
              message.success("Entity deactivated");
              if (selectedClient) fetchEntities(selectedClient.id);
            } catch (err) {
              const errMsg = err instanceof Error ? err.message : "An error occurred";
              message.error(errMsg);
            }
          },
        });
      },
    },
  ];

  return (
    <div className={styles.pageWrap}>

      {/* ── Page header ── */}
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.pageTitle}>Entities</h1>
          <p className={styles.pageSub}>Manage all partnerships and partners under master clients.</p>
        </div>
        <div className={styles.headActions}>
          <button
            className={styles.btnOutline}
            onClick={() => message.info("Import coming soon")}
          >
            Import
          </button>
          <button
            className={styles.btnPrimary}
            onClick={() => setShowTypeModal(true)}
          >
            + Create Entity
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={styles.tabBar}>
        {TABS.map(t => (
          <button
            key={t.key}
            className={activeTab === t.key ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
            {counts[t.key] !== undefined && (
              <span className={styles.tabCount}>{counts[t.key]}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Search row — hidden on Tiering tab ── */}
      {activeTab !== "tiering" && (
        <div className={styles.filterRow}>
          <div className={styles.searchWrap}>
            <SearchOutlined className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Search by name or ENT-id..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      {activeTab === "tiering" ? (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <Segmented
              options={["List", "Diagram"]}
              value={tierView}
              onChange={setTierView}
            />
          </div>
          {!selectedPartnership ? (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", height: "50vh", gap: 12,
            }}>
              <ApartmentOutlined style={{ fontSize: 48, color: "rgba(255,255,255,0.15)" }} />
              <div style={{ fontSize: 15, color: "rgba(255,255,255,0.40)", textAlign: "center" }}>
                Select a partnership from the Partnerships tab to view tiering relationships.
              </div>
            </div>
          ) : tierView === "List" ? (
            <TierList entityId={Number(selectedPartnership.id)} />
          ) : (
            <TierDiagram rootEntityId={Number(selectedPartnership.id)} />
          )}
        </div>
      ) : !selectedClient ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '60vh',
          gap: 12,
        }}>
          <ApartmentOutlined style={{ fontSize: 48, color: 'rgba(255,255,255,0.15)' }} />
          <div style={{
            fontSize: 15,
            color: 'rgba(255,255,255,0.40)',
            textAlign: 'center',
          }}>
            No entities found. Click + Create Entity to add one.
          </div>
        </div>
      ) : loading ? (
        <div className={styles.loadingState}><Spin /></div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.entityTable}>
            <thead>
              <tr>
                <th>ENTITY NAME</th>
                <th style={{ width: 130 }}>TYPE</th>
                <th style={{ width: 150 }}>EIN / SSN</th>
                <th style={{ width: 160 }}>TAX ENTITY TYPE</th>
                <th style={{ width: 120 }}>STATUS</th>
                <th style={{ width: 130 }}>LOCATION</th>
                <th style={{ width: 48 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.emptyCell}>
                    {searchText ? "No entities match your search" : "No entities found for this client. Click + Create Entity to add one."}
                  </td>
                </tr>
              ) : (
                filtered.map(record => {
                  const code = `ENT-${String(record.id).padStart(3, "0")}`;
                  const location = [record.city, record.state].filter(Boolean).join(", ");
                  return (
                    <tr
                      key={record.id}
                      className={styles.tableRow}
                      onClick={() => {
                        if (record.entityType === "Partnership") {
                          setSelectedPartnership({
                            id: String(record.id),
                            name: record.name,
                            entityType: record.entityType,
                          });
                        }
                        navigate(`/entities/${record.id}`, { state: { entity: record } });
                      }}
                    >
                      <td>
                        <div className={styles.cellName}>{record.name}</div>
                        <div className={styles.cellSubId}>{code}</div>
                      </td>
                      <td><EntityTypeBadge type={record.entityType} /></td>
                      <td>
                        {record.einSsn
                          ? <span className={styles.cellMono}>{record.einSsn}</span>
                          : <span className={styles.cellGap}>—</span>
                        }
                      </td>
                      <td>{record.category || <span className={styles.cellGap}>—</span>}</td>
                      <td><StatusBadge status={record.status} /></td>
                      <td>{location || <span className={styles.cellGap}>—</span>}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className={styles.rowActionsCell}>
                          {record.entityType === "Partnership" && (
                            <button
                              className={styles.managePartnersLink}
                              onClick={() => {
                                setSelectedPartnership({
                                  id: String(record.id),
                                  name: record.name,
                                  entityType: record.entityType,
                                });
                                navigate(`/entities/${record.id}`, {
                                  state: { entity: record, initialTab: "partners" },
                                });
                              }}
                            >
                              Manage Partners →
                            </button>
                          )}
                          <Dropdown menu={{ items: rowMenu(record) }} trigger={["click"]}>
                            <button className={styles.actionBtn} aria-label="Actions">
                              <MoreOutlined />
                            </button>
                          </Dropdown>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {filtered.length > 0 && (
            <div className={styles.tableFooter}>
              Showing {filtered.length} of {entities.length} entit{entities.length !== 1 ? "ies" : "y"}
            </div>
          )}
        </div>
      )}

      {/* ── Type selector ── */}
      {showTypeModal && (
        <EntityTypeModal
          onClose={() => setShowTypeModal(false)}
          onSelect={type => {
            setEntityTypeChoice(type);
            setShowTypeModal(false);
            setShowCreateModal(true);
          }}
        />
      )}

      {/* ── Create modal ── */}
      {showCreateModal && (
        <CreateEditModal
          mode="create"
          entityType={entityTypeChoice}
          clientId={selectedClient?.id}
          onClose={() => setShowCreateModal(false)}
          onSaved={() => { setShowCreateModal(false); if (selectedClient) fetchEntities(selectedClient.id); }}
        />
      )}

      {/* ── Edit modal ── */}
      {showEditModal && editTarget && (
        <CreateEditModal
          mode="edit"
          entityType={editTypeChoice}
          entity={editTarget}
          onClose={() => { setShowEditModal(false); setEditTarget(null); }}
          onSaved={() => { setShowEditModal(false); setEditTarget(null); if (selectedClient) fetchEntities(selectedClient.id); }}
        />
      )}
    </div>
  );
};
